import React, { useState, useMemo } from 'react';

import PlannerIcons from '../../components/shared/PlannerIcons';

export default function ProjectGuests({ project, updateProject, safeNavigate }) {
  const [newGuest, setNewGuest] = useState({ name: '', party: '', email: '', rsvp: 'Pending', meal: '', dietary: '' });
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  const guests = project.guests || [];

  const stats = useMemo(() => {
    return {
      total: guests.length,
      attending: guests.filter(g => g.rsvp === 'Attending').length,
      declined: guests.filter(g => g.rsvp === 'Declined').length,
      pending: guests.filter(g => g.rsvp === 'Pending').length,
    };
  }, [guests]);

  const filteredGuests = useMemo(() => {
    return guests.filter(guest => {
      if (filter !== 'All' && guest.rsvp !== filter) return false;
      if (search && !guest.name.toLowerCase().includes(search.toLowerCase()) && !(guest.party || '').toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    }).sort((a, b) => (a.party || '').localeCompare(b.party || '') || a.name.localeCompare(b.name));
  }, [guests, filter, search]);

  const handleAddGuest = (e) => {
    e.preventDefault();
    if (!newGuest.name.trim()) return;

    updateProject(project.id, {
      guests: [...guests, { ...newGuest, id: Date.now().toString() }]
    });
    
    setNewGuest({ name: '', party: newGuest.party, email: '', rsvp: 'Pending', meal: '', dietary: '' });
  };

  const handleRemoveGuest = (id) => {
    if (window.confirm("Remove this guest from the list?")) {
      updateProject(project.id, {
        guests: guests.filter(g => g.id !== id)
      });
    }
  };

  const handleUpdateGuest = (id, field, value) => {
    const updatedGuests = guests.map(g => g.id === id ? { ...g, [field]: value } : g);
    updateProject(project.id, { guests: updatedGuests });
  };

  const exportCsv = () => {
    const headers = ['Name', 'Party/Group', 'Email', 'RSVP', 'Meal', 'Dietary Notes'];
    const rows = guests.map(g => [
      `"${g.name || ''}"`,
      `"${g.party || ''}"`,
      `"${g.email || ''}"`,
      `"${g.rsvp || ''}"`,
      `"${g.meal || ''}"`,
      `"${g.dietary || ''}"`
    ].join(','));
    
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${project.clientNames.replace(/\s+/g, '_')}_GuestList.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in">
        {/* Header & Stats */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-6">
                <div>
                    <h2 className="text-2xl font-bold font-serif text-gray-900">Guest List Manager</h2>
                    <p className="text-sm text-gray-500 mt-1">Track RSVPs, meals, and dietary requirements.</p>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <button onClick={exportCsv} className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm font-bold transition shadow-sm flex items-center justify-center gap-2 flex-1 md:flex-none whitespace-nowrap">
                        📥 Export CSV
                    </button>
                    {safeNavigate && (
                      <button onClick={() => safeNavigate('evite', { id: project.id })} className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 px-4 py-2 rounded-lg text-sm font-bold transition shadow-sm flex items-center justify-center gap-2 flex-1 md:flex-none whitespace-nowrap">
                          <PlannerIcons.Mail /> Send E-Vites
                      </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex flex-col justify-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Guests</p>
                    <div className="flex items-end gap-2">
                        <p className="text-2xl font-black text-gray-900">{stats.total}</p>
                        {project.guestCountGoal > 0 && <span className="text-xs font-bold text-gray-400 mb-1">/ {project.guestCountGoal}</span>}
                    </div>
                </div>
                <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 flex flex-col justify-center cursor-pointer hover:bg-emerald-100 transition" onClick={() => setFilter('Attending')}>
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-1">Attending</p>
                    <p className="text-2xl font-black text-emerald-700">{stats.attending}</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 flex flex-col justify-center cursor-pointer hover:bg-amber-100 transition" onClick={() => setFilter('Pending')}>
                    <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1">Pending</p>
                    <p className="text-2xl font-black text-amber-700">{stats.pending}</p>
                </div>
                <div className="bg-rose-50 p-4 rounded-xl border border-rose-100 flex flex-col justify-center cursor-pointer hover:bg-rose-100 transition" onClick={() => setFilter('Declined')}>
                    <p className="text-[10px] font-bold text-rose-600 uppercase tracking-widest mb-1">Declined</p>
                    <p className="text-2xl font-black text-rose-700">{stats.declined}</p>
                </div>
            </div>
        </div>

        {/* Add Guest Form */}
        <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-200 shadow-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Add Guest</h3>
            <form onSubmit={handleAddGuest} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
                <input 
                    type="text" 
                    required
                    value={newGuest.name} 
                    onChange={e => setNewGuest({...newGuest, name: e.target.value})} 
                    placeholder="Guest Name" 
                    className="lg:col-span-2 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-gray-50"
                />
                <input 
                    type="text" 
                    value={newGuest.party} 
                    onChange={e => setNewGuest({...newGuest, party: e.target.value})} 
                    placeholder="Party / Group (e.g. Smith Family)" 
                    className="lg:col-span-2 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-gray-50"
                />
                <select 
                    value={newGuest.rsvp} 
                    onChange={e => setNewGuest({...newGuest, rsvp: e.target.value})} 
                    className="p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-gray-50"
                >
                    <option value="Pending">Pending</option>
                    <option value="Attending">Attending</option>
                    <option value="Declined">Declined</option>
                </select>
                <input 
                    type="text" 
                    value={newGuest.meal} 
                    onChange={e => setNewGuest({...newGuest, meal: e.target.value})} 
                    placeholder="Meal" 
                    className="p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-gray-50"
                />
                <button 
                    type="submit" 
                    disabled={!newGuest.name.trim()}
                    className="bg-gray-900 hover:bg-black text-white px-4 py-2.5 rounded-lg font-bold transition shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                >
                    <PlannerIcons.Plus /> Add
                </button>
            </form>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="flex gap-2 overflow-x-auto custom-scrollbar pb-2 sm:pb-0">
                {['All', 'Attending', 'Pending', 'Declined'].map(f => (
                    <button 
                        key={f} 
                        onClick={() => setFilter(f)} 
                        className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition border ${filter === f ? 'bg-pink-50 text-pink-700 border-pink-200 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                    >
                        {f}
                    </button>
                ))}
            </div>
            <div className="relative w-full sm:w-64">
                <input 
                    type="text" 
                    value={search} 
                    onChange={e => setSearch(e.target.value)} 
                    placeholder="Search guests..." 
                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-full text-sm outline-none focus:ring-2 focus:ring-pink-500 shadow-sm"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            </div>
        </div>

        {/* Guest Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-100 text-[10px] uppercase tracking-widest text-gray-500">
                            <th className="p-4 font-bold w-1/4">Name</th>
                            <th className="p-4 font-bold w-1/5">Party / Group</th>
                            <th className="p-4 font-bold">RSVP</th>
                            <th className="p-4 font-bold">Meal Choice</th>
                            <th className="p-4 font-bold w-1/5">Dietary Notes</th>
                            <th className="p-4 font-bold text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredGuests.map(guest => (
                            <tr key={guest.id} className="hover:bg-gray-50 transition group">
                                <td className="p-4">
                                    <input 
                                        type="text" 
                                        value={guest.name} 
                                        onChange={(e) => handleUpdateGuest(guest.id, 'name', e.target.value)} 
                                        className="bg-transparent outline-none w-full text-sm font-bold text-gray-900 border border-transparent focus:border-gray-200 focus:bg-white rounded px-2 py-1"
                                    />
                                </td>
                                <td className="p-4">
                                    <input 
                                        type="text" 
                                        value={guest.party || ''} 
                                        onChange={(e) => handleUpdateGuest(guest.id, 'party', e.target.value)} 
                                        placeholder="Add to Party"
                                        className="bg-transparent outline-none w-full text-sm text-gray-600 border border-transparent focus:border-gray-200 focus:bg-white rounded px-2 py-1"
                                    />
                                </td>
                                <td className="p-4">
                                    <select 
                                        value={guest.rsvp} 
                                        onChange={(e) => handleUpdateGuest(guest.id, 'rsvp', e.target.value)} 
                                        className={`text-xs font-bold uppercase tracking-wider rounded-md px-2 py-1 outline-none border cursor-pointer ${
                                            guest.rsvp === 'Attending' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                            guest.rsvp === 'Declined' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                                            'bg-amber-50 text-amber-700 border-amber-200'
                                        }`}
                                    >
                                        <option value="Pending">Pending</option>
                                        <option value="Attending">Attending</option>
                                        <option value="Declined">Declined</option>
                                    </select>
                                </td>
                                <td className="p-4">
                                    <input 
                                        type="text" 
                                        value={guest.meal || ''} 
                                        onChange={(e) => handleUpdateGuest(guest.id, 'meal', e.target.value)} 
                                        placeholder="N/A"
                                        className="bg-transparent outline-none w-full text-sm text-gray-600 border border-transparent focus:border-gray-200 focus:bg-white rounded px-2 py-1"
                                    />
                                </td>
                                <td className="p-4">
                                    <input 
                                        type="text" 
                                        value={guest.dietary || ''} 
                                        onChange={(e) => handleUpdateGuest(guest.id, 'dietary', e.target.value)} 
                                        placeholder="Allergies?"
                                        className={`bg-transparent outline-none w-full text-sm border border-transparent focus:border-gray-200 focus:bg-white rounded px-2 py-1 ${guest.dietary ? 'text-rose-600 font-medium' : 'text-gray-400'}`}
                                    />
                                </td>
                                <td className="p-4 text-center">
                                    <button
                                        onClick={() => handleRemoveGuest(guest.id)}
                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition opacity-0 group-hover:opacity-100 focus:opacity-100"
                                        title="Delete Guest"
                                    >
                                        <PlannerIcons.Trash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {filteredGuests.length === 0 && (
                            <tr>
                                <td colSpan="6" className="p-12 text-center">
                                    <div className="text-4xl mb-4 opacity-50">🍽️</div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-1">No guests found</h3>
                                    <p className="text-sm text-gray-500">
                                        {search || filter !== 'All' ? 'Try adjusting your filters or search.' : 'Add your first guest above!'}
                                    </p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
  );
}